import React, { useEffect, useRef, useState } from "react";
import { Mic, Loader2, X, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { saveRecording, getRecording, deleteRecording } from "@/lib/voice-store";

/**
 * Durable microphone button.
 *
 * Records via MediaRecorder, saves the audio blob locally to IndexedDB every
 * 5 seconds so a tab-close, phone call, or network drop never loses the user's
 * input. On stop, uploads to /api/voice/transcribe. If the upload fails, the
 * blob stays in IndexedDB and a retry pill appears.
 */
export function MicButton({ onTranscript, familyId, recordingId = "active" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [retryablePending, setRetryablePending] = useState(false);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("audio/webm");
  const streamRef = useRef(null);
  const startTimeRef = useRef(0);
  const elapsedTimerRef = useRef(null);
  const persistTimerRef = useRef(null);

  // On mount: detect any pending unfinished recording for THIS family
  useEffect(() => {
    if (!familyId) return;
    getRecording(`${familyId}:${recordingId}`)
      .then((row) => {
        if (row && row.blob && row.blob.size > 0) setRetryablePending(true);
      })
      .catch(() => {});
  }, [familyId, recordingId]);

  const startTimers = () => {
    elapsedTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 250);
    // Persist to IndexedDB every 5 seconds — survives tab-close / phone-call
    persistTimerRef.current = setInterval(async () => {
      if (chunksRef.current.length === 0) return;
      const blob = new Blob(chunksRef.current, { type: mimeRef.current });
      try {
        await saveRecording({
          id: `${familyId}:${recordingId}`,
          blob,
          mime: mimeRef.current,
          durationMs: Date.now() - startTimeRef.current,
          familyId,
        });
      } catch (_e) { /* silent */ }
    }, 5000);
  };

  const clearTimers = () => {
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    if (persistTimerRef.current) clearInterval(persistTimerRef.current);
    elapsedTimerRef.current = null;
    persistTimerRef.current = null;
  };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Voice not supported here");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = onStopHandler;
      rec.start(1000); // chunk every 1s — keeps chunksRef fresh
      recRef.current = rec;
      startTimeRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      startTimers();
    } catch (_e) {
      setError("Microphone blocked");
    }
  };

  const onStopHandler = async () => {
    clearTimers();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    if (!blob.size) { setRecording(false); return; }
    setBusy(true);
    setRecording(false);
    // Always save a final copy locally BEFORE network upload
    try {
      await saveRecording({
        id: `${familyId}:${recordingId}`,
        blob,
        mime: mimeRef.current,
        durationMs: Date.now() - startTimeRef.current,
        familyId,
      });
    } catch (_e) { /* swallow */ }
    await transcribeAndClear(blob, mimeRef.current);
  };

  const transcribeAndClear = async (blob, mime) => {
    try {
      const fd = new FormData();
      fd.append("file", blob, mime.includes("webm") ? "voice.webm" : "voice.m4a");
      const { data } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.text) onTranscript(data.text);
      await deleteRecording(`${familyId}:${recordingId}`);
      setRetryablePending(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't reach Kinly — saved locally");
      setRetryablePending(true); // keep it in IDB; show retry UI
    } finally {
      setBusy(false);
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const retry = async () => {
    setError("");
    setBusy(true);
    try {
      const row = await getRecording(`${familyId}:${recordingId}`);
      if (!row?.blob) { setRetryablePending(false); setBusy(false); return; }
      await transcribeAndClear(row.blob, row.mime);
    } catch (e) {
      setError("Retry failed — try again or discard");
      setBusy(false);
    }
  };

  const discard = async () => {
    await deleteRecording(`${familyId}:${recordingId}`);
    setRetryablePending(false);
    setError("");
  };

  const seconds = Math.floor(elapsedMs / 1000);
  const elapsedLabel = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        data-testid="mic-button"
        onClick={recording ? stop : start}
        disabled={busy}
        className={[
          "w-9 h-9 rounded-full tap flex items-center justify-center shrink-0",
          recording
            ? "bg-destructive text-destructive-foreground animate-pulse"
            : "bg-secondary text-foreground hover:bg-secondary/70",
        ].join(" ")}
        aria-label={recording ? "Stop recording" : "Start voice"}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
         recording ? <X className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>

      {recording && (
        <span data-testid="mic-elapsed" className="text-[12px] tabular-nums font-mono text-destructive">
          ● {elapsedLabel}
        </span>
      )}

      {retryablePending && !recording && !busy && (
        <button
          type="button"
          data-testid="mic-retry"
          onClick={retry}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-secondary border border-border text-[11px] tap"
          title="A previous voice recording is saved locally — retry uploading"
        >
          <RefreshCw className="w-3 h-3" /> Retry upload
        </button>
      )}
      {retryablePending && !recording && !busy && (
        <button
          type="button"
          data-testid="mic-discard"
          onClick={discard}
          className="text-[11px] text-muted-foreground tap underline"
          title="Discard the saved recording"
        >
          Discard
        </button>
      )}

      {error && (
        <span data-testid="mic-error" className="text-[11px] text-destructive ml-1">
          {error}
        </span>
      )}
    </div>
  );
}
